import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calcTieredCost } from './utils/tier-pricing.util';
import { calcTotalMonthlyPrice, calcTotalYearlyPrice, calcRawAddOns } from './utils/calc-total-price.util';
import { PLANS_YEARLY_DISCOUNT } from '@/constants';
import { monetaryFormat } from '@/utils/monetary-format';
import { round } from '@/utils/round';
import { PlanPriceInterval } from '@/graphql/generated/types.generated';
import type { MappedPlan } from './utils/map-graphql-to-calculator.util';
import type { AddOnLine } from './utils/calc-total-price.util';

/** Single label + value row with locked right-side price column */
const LineItem = ({ label, value, color }: { label: string; value: string; color?: string }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
        <Typography variant="body2" color={color ?? 'text.secondary'} sx={{ minWidth: 0 }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={600}
            color={color ?? 'text.primary'}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
            {value}
        </Typography>
    </Stack>
);

interface PriceSummaryProps {
    plan: MappedPlan;
    addOnLines: AddOnLine[];
    interval: PlanPriceInterval;
    /** Feature codes in the order the user activated them — drives the breakdown order. */
    activationOrder?: string[];
    onGetStarted?: () => void;
    loading?: boolean;
    note?: string;
}

export const PriceSummary = ({
    plan,
    addOnLines,
    interval,
    activationOrder = [],
    onGetStarted,
    loading,
    note,
}: PriceSummaryProps) => {
    const { t } = useTranslation();

    // Always show the raw monthly price on the plan line — yearly plan saving is shown separately
    const basePlanMonthly = plan.monthlyPrice;

    // ── Numeric lines ─────────────────────────────────────────────────────────
    const numericLines = addOnLines.filter(
        (l) =>
            (l.extraQty ?? 0) > 0 &&
            (l.feature.addOnPricePerUnit !== undefined || (l.feature.addOnTiers?.length ?? 0) > 0),
    );

    // ── Boolean lines ─────────────────────────────────────────────────────────
    const boolLines = addOnLines.filter((l) => l.boolEnabled === true && l.feature.addOnPrice !== undefined);

    // ── Enum lines — single-select tier ──────────────────────────────────────
    const enumLineExtras = addOnLines
        .filter((l) => {
            if (!l.feature.addOnOptions || !l.enumValue) return false;
            if (l.enumValue === l.feature.displayValue) return false;
            const sel = l.feature.addOnOptions.find((o) => o.value === l.enumValue)?.price ?? 0;
            return sel > 0;
        })
        .map((l) => {
            const sel = l.feature.addOnOptions?.find((o) => o.value === l.enumValue)?.price ?? 0;
            return { ...l, extraCost: sel };
        });

    // ── Merge all add-on lines into one list, ordered by when the user added them ──
    const perMonth = t('billing.calculator.per-month');
    const addOnRows: { code: string; label: string; value: string }[] = [
        ...numericLines.map(({ feature, extraQty }) => ({
            code: feature.code,
            label: `+${extraQty} · ${t(feature.label)}`,
            value: `${monetaryFormat(
                feature.addOnTiers?.length
                    ? calcTieredCost(extraQty ?? 0, feature.addOnTiers, feature.value)
                    : (extraQty ?? 0) * (feature.addOnPricePerUnit ?? 0),
                plan.currency,
            )}${perMonth}`,
        })),
        ...boolLines.map(({ feature }) => ({
            code: feature.code,
            label: t(feature.label),
            value: `${monetaryFormat(feature.addOnPrice ?? 0, plan.currency)}${perMonth}`,
        })),
        ...enumLineExtras.map(({ feature, enumValue, extraCost }) => ({
            code: feature.code,
            label: `${t(feature.label)} → ${t(feature.addOnOptions?.find((o) => o.value === enumValue)?.label ?? '')}`,
            value: `${monetaryFormat(extraCost, plan.currency)}${perMonth}`,
        })),
    ].sort((a, b) => {
        const ra = activationOrder.indexOf(a.code);
        const rb = activationOrder.indexOf(b.code);
        return (ra === -1 ? Number.MAX_SAFE_INTEGER : ra) - (rb === -1 ? Number.MAX_SAFE_INTEGER : rb);
    });

    const rawAddOns = calcRawAddOns(addOnLines);
    const annualSaving =
        interval === PlanPriceInterval.Year
            ? round(plan.monthlyPrice - plan.yearlyMonthlyPrice + rawAddOns * (1 - PLANS_YEARLY_DISCOUNT), 2)
            : 0;
    const monthlyTotal = calcTotalMonthlyPrice(plan, addOnLines, interval);
    const yearlyTotal = calcTotalYearlyPrice(plan, addOnLines, interval);

    return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper' }}>
            <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {t('billing.calculator.price-summary.title')}
                </Typography>

                <Stack spacing={1}>
                    {/* Base plan — always raw monthly price */}
                    <LineItem
                        label={plan.name}
                        value={`${monetaryFormat(basePlanMonthly, plan.currency)}${t('billing.calculator.per-month')}`}
                    />

                    {/* Add-ons — ordered by when the user added them */}
                    {addOnRows.map((row) => (
                        <LineItem key={row.code} label={row.label} value={row.value} />
                    ))}
                </Stack>

                {/* Annual saving line */}
                {annualSaving > 0 && (
                    <LineItem
                        label={t('billing.calculator.price-summary.annual-saving')}
                        value={`-${monetaryFormat(annualSaving, plan.currency)}${t('billing.calculator.per-month')}`}
                        color="success.main"
                    />
                )}

                {note && note.trim() && <LineItem label={note.trim()} value="" color="info.main" />}

                <Divider />

                {/* Total */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={700}>
                        {t('billing.calculator.price-summary.total')}
                    </Typography>
                    <Box textAlign="right">
                        <Typography variant="h6" fontWeight={800} lineHeight={1}>
                            {monetaryFormat(monthlyTotal, plan.currency)}
                            {t('billing.calculator.per-month')}
                        </Typography>
                        {interval === PlanPriceInterval.Year && (
                            <Typography variant="caption" color="text.secondary">
                                {t('billing.calculator.price-summary.billed-yearly', {
                                    price: monetaryFormat(yearlyTotal, plan.currency),
                                })}
                            </Typography>
                        )}
                    </Box>
                </Stack>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    loading={loading}
                    onClick={onGetStarted}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                    {t('trial-empty-state.subscribe-now')}
                </Button>

                <Typography variant="caption" color="text.disabled" textAlign="center" display="block">
                    {t('str.subs.vat-excluded')}
                </Typography>
            </Stack>
        </Paper>
    );
};
